import { useEffect, useState } from 'react';
import {
  Database,
  TrendingUp,
  RefreshCw,
  Calendar,
  Server,
  Clock,
  Download,
} from 'lucide-react';

interface CacheStats {
  dateRange: { startDate: string; endDate: string };
  globalCache: {
    totalEntries: number;
    landlineCount: number;
    tcpaRiskCount: number;
  };
  goodPhoneCache: {
    activeEntries: number;
    ttlDays: number;
  };
  performance: {
    totalProcessed: number;
    totalCacheHits: number;
    globalCacheHits: number;
    dailyCacheHits: number;
    apiCalls: number;
    cacheHitRate: number;
  };
  financial: {
    apiCallsSaved: number;
    costSaved: number;
    profitFromCache: number;
  };
}

export default function AdminCacheStats() {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date range - default to last 30 days
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(now.toISOString().split('T')[0]);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    fetchCacheStats();
  }, [startDate, endDate]);

  const fetchCacheStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/cache/stats?${params.toString()}`,
        { credentials: 'include' }
      );

      const data = await response.json() as any;

      if (data.success) {
        setStats(data);
      } else {
        setError(data.error || 'Failed to fetch cache stats');
      }
    } catch (err) {
      console.error('[CacheStats] Error:', err);
      setError('Failed to fetch cache statistics');
    } finally {
      setLoading(false);
    }
  };

  // The server streams ~290k rows; response.blob() rejects if the stream is
  // errored mid-way, so a partial file is never saved.
  const exportGlobalCache = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/cache/export`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `global-phone-cache-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setExportError(
        `Export failed: ${err instanceof Error ? err.message : 'unknown error'}. No file was saved.`
      );
    } finally {
      setExporting(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Cache Totals - Always visible, not date-dependent */}
      {stats && (
        <div>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Current Cache Totals</h2>
              <p className="text-sm text-gray-500">Point-in-time snapshot of cached entries</p>
            </div>
            <button
              onClick={exportGlobalCache}
              disabled={exporting}
              className="flex items-center space-x-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              <span>{exporting ? 'Exporting… (may take a minute)' : 'Export Global Cache CSV'}</span>
            </button>
          </div>
          {exportError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
              {exportError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Global Cache"
              value={stats.globalCache.totalEntries.toLocaleString()}
              subtitle="Total bad numbers"
              icon={Database}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
            />
            <StatCard
              title="Landlines"
              value={stats.globalCache.landlineCount.toLocaleString()}
              subtitle="In global cache"
              icon={Server}
              iconBg="bg-orange-100"
              iconColor="text-orange-600"
            />
            <StatCard
              title="TCPA Risks"
              value={stats.globalCache.tcpaRiskCount.toLocaleString()}
              subtitle="In global cache"
              icon={Server}
              iconBg="bg-red-100"
              iconColor="text-red-600"
            />
            <StatCard
              title="Good Phone Cache"
              value={stats.goodPhoneCache.activeEntries.toLocaleString()}
              subtitle={`${stats.goodPhoneCache.ttlDays}-day TTL`}
              icon={Clock}
              iconBg="bg-green-100"
              iconColor="text-green-600"
            />
          </div>
        </div>
      )}

      {/* Date Range Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartDate(newStart);
                if (newStart > endDate) {
                  setEndDate(newStart);
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const newEnd = e.target.value;
                setEndDate(newEnd);
                if (newEnd < startDate) {
                  setStartDate(newEnd);
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={fetchCacheStats}
            disabled={loading}
            className="ml-auto p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* Performance Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Performance ({startDate} to {endDate})
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Cache Hit Rate */}
                <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-600">Cache Hit Rate</p>
                  <p className="text-4xl font-bold text-blue-600 mt-2">
                    {stats.performance.cacheHitRate.toFixed(1)}%
                  </p>
                </div>

                {/* Total Processed */}
                <div>
                  <p className="text-sm text-gray-500">Total Processed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.performance.totalProcessed.toLocaleString()}
                  </p>
                </div>

                {/* Cache Hits */}
                <div>
                  <p className="text-sm text-gray-500">Total Cache Hits</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.performance.totalCacheHits.toLocaleString()}
                  </p>
                  <div className="mt-1 text-xs text-gray-500">
                    Global: {stats.performance.globalCacheHits.toLocaleString()} |
                    Daily: {stats.performance.dailyCacheHits.toLocaleString()}
                  </div>
                </div>

                {/* API Calls */}
                <div>
                  <p className="text-sm text-gray-500">API Calls Made</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {stats.performance.apiCalls.toLocaleString()}
                  </p>
                </div>

                {/* API Calls Saved */}
                <div>
                  <p className="text-sm text-gray-500">API Calls Saved</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.financial.apiCallsSaved.toLocaleString()}
                  </p>
                </div>

                {/* Cost Saved */}
                <div>
                  <p className="text-sm text-gray-500">Cost Saved</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${stats.financial.costSaved.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Impact */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 font-medium">Profit from Cache</p>
                <p className="text-3xl font-bold mt-1">
                  ${stats.financial.profitFromCache.toFixed(2)}
                </p>
                <p className="text-emerald-200 text-sm mt-2">
                  Revenue generated from cached contacts (zero API cost)
                </p>
              </div>
              <div className="bg-white/20 p-4 rounded-xl">
                <TrendingUp className="w-8 h-8" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Stat Card
function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: typeof Database;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}
